<template>
  <div class="pa-4">
    <summary-pill-bar
      :pills="summaryPills"
      :active-key="activeFilter"
      class="mb-3"
      @select="toggleFilter"
    />

    <v-row class="mb-2" dense align="center">
      <v-col cols="12" sm="6" md="4">
        <text-field
          v-model="search"
          kind="search"
          clearable
          :label="$t('search')"
          :placeholder="$t('contracts_search_placeholder')"
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <dropdown
          v-model="activeFilter"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          :label="$t('all_statuses')"
          clearable
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Desktop table -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="filteredDirectory"
      :loading="loading"
      :loading-text="$t('loading_data')"
      :item-class="rowClass"
      :custom-sort="sortByPriority"
      sort-by="status"
      class="striped-table ordera-box-shadow"
      mobile-breakpoint="0"
    >
      <template v-slot:item.naziv="{ item }">
        <span class="font-weight-medium">{{ toLatin(item.naziv) }}</span>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">
          {{ statusLabel(item) }}
        </v-chip>
      </template>

      <template v-slot:item.contract_number="{ item }">
        <div v-if="item.hasContract">
          <div>{{ item.contract_number }}</div>
          <div v-if="item.contract_date" class="caption grey--text">
            {{ $t("contract_date") }}: {{ localeDate(item.contract_date) }}
          </div>
          <div class="caption grey--text">
            <template v-if="item.valid_from">
              {{ localeDate(item.valid_from) }} –
              {{
                item.vazi_do ? localeDate(item.vazi_do) : $t("contract_no_end_date_short")
              }}
            </template>
            <template v-else-if="item.vazi_do"
              >{{ $t("until") }} {{ localeDate(item.vazi_do) }}</template
            >
            · {{ $t("contract_items", { count: item.broj_usluga }) }}
            <template v-if="item.opis"
              >· {{ $t("contract_commission_prefix") }} {{ item.opis }}</template
            >
          </div>
          <div
            v-if="item.status === 'ISTEKAO'"
            class="caption warning--text font-weight-medium"
          >
            {{ $t("still_being_billed") }}
          </div>
        </div>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="text-end">
          <v-btn
            v-if="item.hasContract"
            small
            :outlined="isHealthy(item.status)"
            :color="isHealthy(item.status) ? 'primary' : 'error'"
            @click="$emit('open-contract', item.raw)"
          >
            {{ isHealthy(item.status) ? $t("open") : $t("renew") }}
          </v-btn>
          <v-btn
            v-else
            small
            color="warning"
            depressed
            @click="$emit('enter-price', item.raw)"
          >
            {{ $t("enter_price") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:no-data>
        <empty-state icon="mdi-file-document-outline" :title="$t('nothing_pending')" />
      </template>
    </v-data-table>

    <!-- Mobile cards -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="filteredDirectory"
      :loading="loading"
      item-key="key"
      :show-row-number="false"
      :fields-per-row="2"
      :bold-fields="['naziv']"
      empty-icon="mdi-file-document-outline"
      :empty-title="$t('nothing_pending')"
    >
      <template v-slot:item.naziv="{ item }">{{ toLatin(item.naziv) }}</template>

      <template v-slot:item.status="{ item }">
        <v-chip x-small :color="statusColor(item.status)" text-color="white">
          {{ statusLabel(item) }}
        </v-chip>
      </template>

      <template v-slot:item.contract_number="{ item }">
        <div v-if="item.hasContract">
          <div>{{ item.contract_number }}</div>
          <div v-if="item.contract_date" class="caption grey--text">
            {{ $t("contract_date") }}: {{ localeDate(item.contract_date) }}
          </div>
          <div class="caption grey--text">
            <template v-if="item.valid_from">
              {{ localeDate(item.valid_from) }} –
              {{
                item.vazi_do ? localeDate(item.vazi_do) : $t("contract_no_end_date_short")
              }}
            </template>
            <template v-else-if="item.vazi_do"
              >{{ $t("until") }} {{ localeDate(item.vazi_do) }}</template
            >
            · {{ $t("contract_items", { count: item.broj_usluga }) }}
            <template v-if="item.opis"
              >· {{ $t("contract_commission_prefix") }} {{ item.opis }}</template
            >
          </div>
          <div
            v-if="item.status === 'ISTEKAO'"
            class="caption warning--text font-weight-medium"
          >
            {{ $t("still_being_billed") }}
          </div>
        </div>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.hasContract"
          small
          block
          :outlined="isHealthy(item.status)"
          :color="isHealthy(item.status) ? 'primary' : 'error'"
          @click="$emit('open-contract', item.raw)"
        >
          {{ isHealthy(item.status) ? $t("open") : $t("renew") }}
        </v-btn>
        <v-btn
          v-else
          small
          block
          color="warning"
          depressed
          @click="$emit('enter-price', item.raw)"
        >
          {{ $t("enter_price") }}
        </v-btn>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import SummaryPillBar from "./SummaryPillBar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import {
  contractStatusColor,
  contractStatusLabel,
  isHealthyContractStatus,
  normalizeContractStatus,
} from "@/utils/contractStatus";

const STATUS_PRIORITY = {
  "VAŽI": 0,
  "BEZ ROKA": 0,
  "USKORO ISTIČE": 1,
  ISTEKAO: 2,
  BEZ_UGOVORA: 3,
};

export default {
  name: "ContractsOverviewTab",
  components: { SummaryPillBar, EmptyState, MobileDataCards },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    contracts: {
      type: Array,
      default: () => [],
    },
    noContractList: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      search: "",
      activeFilter: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("restaurant"), value: "naziv", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true, width: "170px" },
        { text: this.$t("contract_number"), value: "contract_number", sortable: false },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "end" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("restaurant"), value: "naziv", cols: 12 },
        { text: this.$t("status"), value: "status", cols: 12 },
        { text: this.$t("contract_number"), value: "contract_number", cols: 12 },
        { text: "", value: "actions", cols: 12 },
      ];
    },
    statusOptions() {
      return [
        { value: "ISTEKAO", text: this.$t("status_expired") },
        { value: "USKORO ISTIČE", text: this.$t("status_expiring_soon") },
        { value: "VAŽI", text: this.$t("contract_status_active") },
        { value: "BEZ ROKA", text: this.$t("contract_status_no_deadline") },
        { value: "BEZ_UGOVORA", text: this.$t("no_contracts") },
      ];
    },
    summaryPills() {
      return [
        {
          key: "ISTEKAO",
          label: this.$t("status_expired"),
          count: this.summary.istekli ?? this.countByStatus("ISTEKAO"),
          color: contractStatusColor("ISTEKAO"),
        },
        {
          key: "USKORO ISTIČE",
          label: this.$t("status_expiring_soon"),
          count: this.summary.uskoro ?? this.countByStatus("USKORO ISTIČE"),
          color: contractStatusColor("USKORO ISTIČE"),
        },
        {
          key: "VAŽI",
          label: this.$t("status_active_count"),
          count:
            this.summary.vaze ??
            this.contracts.filter((c) => isHealthyContractStatus(c.status)).length,
          color: contractStatusColor("VAŽI"),
        },
        {
          key: "BEZ_UGOVORA",
          label: this.$t("no_contracts"),
          count: this.summary.bez_ugovora ?? this.noContractList.length,
          color: "grey",
        },
      ];
    },
    directory() {
      const withContracts = this.contracts.map((c) => ({
        key: "c-" + c.contract_id,
        naziv: c.naziv,
        hasContract: true,
        status: normalizeContractStatus(c.status),
        contract_number: c.contract_number,
        // /contracts/expiring šalje `vazi_od` (spec ga ne navodi); `valid_from` je ostao za slučaj da ga backend preimenuje
        valid_from: c.vazi_od ?? c.valid_from,
        contract_date: c.contract_date,
        vazi_do: c.vazi_do,
        broj_usluga: c.broj_usluga,
        opis: c.opis,
        dana_do_isteka: c.dana_do_isteka,
        raw: c,
      }));
      const withoutContracts = this.noContractList.map((i) => ({
        key: "n-" + i.restaurant_id,
        naziv: i.naziv,
        hasContract: false,
        status: "BEZ_UGOVORA",
        contract_number: "",
        raw: i,
      }));
      return [...withContracts, ...withoutContracts];
    },
    filteredDirectory() {
      const statusFiltered = this.directory.filter((row) => {
        if (this.activeFilter === "BEZ_UGOVORA") return row.status === "BEZ_UGOVORA";
        if (this.activeFilter === "VAŽI") {
          return row.hasContract && isHealthyContractStatus(row.status);
        }
        if (this.activeFilter) return row.status === this.activeFilter;
        return true;
      });
      return this.customSearch(statusFiltered, [{ value: "naziv" }, { value: "contract_number" }], this.search);
    },
  },
  methods: {
    isHealthy(status) {
      return isHealthyContractStatus(status);
    },
    statusColor(status) {
      return status === "BEZ_UGOVORA" ? "grey" : contractStatusColor(status);
    },
    statusLabel(item) {
      if (!item.hasContract) return this.$t("no_contracts");
      return contractStatusLabel(item.raw, this.$t.bind(this));
    },
    countByStatus(status) {
      return this.contracts.filter((c) => normalizeContractStatus(c.status) === status).length;
    },
    toggleFilter(key) {
      this.activeFilter = this.activeFilter === key ? null : key;
    },
    rowClass(item) {
      if (item.status === "ISTEKAO") return "contract-row--expired";
      if (item.status === "USKORO ISTIČE") return "contract-row--expiring";
      return "";
    },
    sortByPriority(items, sortBy, sortDesc) {
      if (!sortBy.length) return items;
      const [key] = sortBy;
      const desc = sortDesc[0];
      const sorted = [...items].sort((a, b) => {
        let result;
        if (key === "status") {
          result = (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9);
        } else {
          result = String(a[key] ?? "").localeCompare(String(b[key] ?? ""), undefined, {
            numeric: true,
          });
        }
        return desc ? -result : result;
      });
      return sorted;
    },
  },
};
</script>

<style scoped>
.contract-row--expired {
  box-shadow: inset 4px 0 0 0 #b71c1c;
}
.contract-row--expiring {
  box-shadow: inset 4px 0 0 0 #fb8c00;
}
</style>
